<script setup>
import { ref, computed, onBeforeMount } from "vue";
import PresentationConfiguration from "@/components/agents/PresentationConfiguration.vue";
import { createPresentationModel, validatePresentation } from "@/utils/agentPresentation";
import { withAgentPresentation } from "@/utils/agentPayload";
import { isTeamMember, readAccessSession } from "@/utils/accessControl";
const props = defineProps({
  closeModal: Function,
  agentTypesOptions: {
    type: Array,
    default: () => [],
  },
  dataCollectionsGridOptions: {
    type: Array,
    default: () => [],
  },
  voiceOptions: {
    type: Array,
    default: () => [],
  },
  avatarOptions: {
    type: Array,
    default: () => [],
  },
  llmModelOptions: {
    type: Array,
    default: () => [],
  },
  sttModelOptions: {
    type: Array,
    default: () => [],
  },
  presentationThemeOptions: {
    type: Array,
    default: () => [],
  },
  presentationImageOptions: {
    type: Array,
    default: () => [],
  },
});
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useAgentsStore } from "@/stores/agents";

const uiStore = useUiStore();
const agentsStore = useAgentsStore();
const emit = defineEmits(["refresh"]);
const company_id = useCompanyStore().companyId;
const getModalInfo = computed(() => uiStore.modalData);
const getModalAction = computed(() => uiStore.modalAction);
const canManageTeamProfile = isTeamMember(readAccessSession());

let id = ref(null);
let name = ref(null);
let description = ref(null);
let welcome_message = ref(null);
let prompt = ref(null);
let agent_type = ref(null);
// let data_collection = ref([]);
let status = ref(null);
let statusOptions = ref(["Active", "Testing", "Disabled"]);
let voice_id = ref(null);
let avatar_id = ref(null);
let presentation = ref(createPresentationModel());
let presentationRevision = ref(null);
let llm_model = ref(null);
let stt_model = ref(null);
let isSubmitted = ref(false);
let conflictMessage = ref("");
let selectedDataCollections = ref([]);
let isSelectAllDataCollections = ref(false);
const presentationErrors = computed(() => validatePresentation(presentation.value));
const hasPresentationErrors = computed(() => Object.keys(presentationErrors.value).length > 0);

// const getDataCollectionIds = () => {
//   return (
//     data_collection.value.length > 0 && data_collection.value.map((dc) => dc.id)
//   );
// };

const onSelectAllDataCollectionsChange = (event) => {
  selectedDataCollections.value = event.checked
    ? props.dataCollectionsGridOptions.map((item) => item.id)
    : [];

  isSelectAllDataCollections.value = event.checked;
};

const onChangeDataCollectionsVal = (event) => {
  selectedDataCollections.value = event.value;
  if (
    selectedDataCollections.value.length ===
    props.dataCollectionsGridOptions.length
  ) {
    isSelectAllDataCollections.value = true;
  } else if (
    selectedDataCollections.value.length <
    props.dataCollectionsGridOptions.length
  ) {
    isSelectAllDataCollections.value = false;
  }
};

const getDataCollectionName = (id) => {
  const found = props.dataCollectionsGridOptions.find((s) => s.id === id);
  return found ? found.name : id;
};

const removeDataCollection = (dataCollectionId) => {
  selectedDataCollections.value = selectedDataCollections.value.filter(
    (id) => id !== dataCollectionId,
  );
};

const fetchVoiceById = (voiceId) => {
  agentsStore
    .fetchAgentVoiceById({ voiceId })
    .then((res) => {
      const audioUrl = URL.createObjectURL(res);
      const audio = new Audio(audioUrl);
      audio.play();
    })
    .catch(() => {});
};

const onChangeVoiceVal = (event) => {
  voice_id.value = event.value;
  fetchVoiceById(event.value);
};

const hydrateAgent = (agent) => {
  id.value = agent.id;
  name.value = agent.name;
  description.value = agent.description;
  welcome_message.value = agent.welcome_message;
  prompt.value = agent.prompt;
  agent_type.value = agent.agentTypeInfo.name;
  selectedDataCollections.value = agent.data_collections.map((dc) => dc.id);
  status.value = agent.status.charAt(0).toUpperCase() + agent.status.slice(1);
  voice_id.value = agent.voice_id;
  avatar_id.value = agent.avatar_id;
  presentation.value = createPresentationModel(agent.presentation);
  presentationRevision.value = agent.presentation_revision;
  llm_model.value = agent.llm_model_id;
  stt_model.value = agent.stt_model_id;
};

const reloadAfterConflict = async () => {
  const response = await agentsStore.fetchAgentById({ companyId: company_id, id: id.value });
  const latest = response.data;
  hydrateAgent(latest);
  uiStore.setModalData(latest);
  conflictMessage.value = "This presentation changed elsewhere. The latest version has been reloaded; review it before saving again.";
  emit("refresh");
};

const onSubmit = async () => {
  isSubmitted.value = true;
  conflictMessage.value = "";

  const data = withAgentPresentation({
    id: id.value,
    name: name.value,
    description: description.value,
    welcome_message: welcome_message.value,
    agent_type: agent_type.value,
    data_collection_ids: selectedDataCollections.value,
    status: status.value.toLowerCase(),
    voice_id: voice_id.value,
    avatar_id: avatar_id.value,
    llm_model_id: llm_model.value,
    stt_model_id: stt_model.value,
    company_id,
  }, {
    presentation: presentation.value,
    originalPresentation: getModalInfo.value.presentation,
    canManageTeamProfile,
    prompt: prompt.value,
    presentationRevision: presentationRevision.value,
  });
  try {
    await agentsStore.editAgent(data);
    resetStoreData();
    emit("refresh");
  } catch (error) {
    if (error?.status === 409) {
      try {
        await reloadAfterConflict();
      } catch {
        conflictMessage.value = "This presentation changed elsewhere. Close and reopen the editor to load the latest version.";
      }
    }
    isSubmitted.value = false;
  }
};

const resetStoreData = () => {
  uiStore.setModalState(false);
  uiStore.setModalData({});
  uiStore.setModalAction(null);
};

onBeforeMount(() => {
  if (getModalInfo.value && getModalAction.value == "edit_agent") {
    hydrateAgent(getModalInfo.value);
  }
});
</script>
<template>
  <DModal
    v-if="getModalAction === 'edit_agent'"
    :visible="uiStore.modalState"
    @update:visible="
      (val) => {
        if (!val) resetStoreData();
      }
    "
    :main_classes="[]"
    :dialog_classes="[]"
  >
    <template #title> Edit Agent </template>

    <template #body>
      <div v-if="conflictMessage" class="alert alert-warning presentation-conflict" role="alert">
        {{ conflictMessage }}
      </div>
      <!-- <div class="relative mb-3">
        <span v-if="agent_type" class="multiselect-title-span">Agent Type</span>
        <VMultiselect
          v-model="agent_type"
          :options="props.agentTypesOptions"
          :disabled="!props.agentTypesOptions.length"
          :loading="!props.agentTypesOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          placeholder="Agent Type"
          trackBy="name"
          label="description"
        />
      </div> -->

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="agent_type"
          inputId="agent_type"
          :options="props.agentTypesOptions"
          :disabled="!props.agentTypesOptions.length"
          :loading="!props.agentTypesOptions.length"
          optionLabel="description"
          optionValue="name"
          class="w-full"
          filter
          filterPlaceholder="Search by agent type"
        />
        <label for="agent_type">Agent Type</label>
      </FloatLabel>

      <!-- <div class="grid gap-10 mb-3">
        <span class="hint-input mb-1"><b>Description:</b> Basic product info (no inventory/stores/pricing).</span>  
        <span class="hint-input"><b>Example Use Cases:</b> Pharmacy, Simple Retailer</span>  
      </div> -->

      <div class="mb-3">
        <DInput
          :value="name"
          @input="name = $event.target.value"
          label="Agent Name"
          width="100%"
          maxlength="21"
        />
      </div>

      <FloatLabel class="w-full mb-3">
        <PTextarea
          v-model="description"
          maxlength="55"
          id="agentDescription"
          class="d-textarea resize-none h-105 w-full"
          autoResize
        />
        <label for="agentDescription">Description</label>
      </FloatLabel>

      <FloatLabel class="w-full mb-3">
        <PTextarea
          v-model="welcome_message"
          id="agentWelcomeMessage"
          class="d-textarea resize-none h-105 w-full"
          maxlength="180"
          autoResize
        />
        <label for="agentWelcomeMessage">Welcome Message</label>
      </FloatLabel>

      <!-- <div class="relative mb-3">
        <span v-if="status" class="multiselect-title-span">Status</span>
        <VMultiselect
          v-model="status"
          :options="statusOptions"
          :disabled="!statusOptions.length"
          :loading="!statusOptions.length"
          :searchable="true"
          :close-on-select="true"
          :show-labels="false"
          placeholder="Status"
        />
      </div> -->

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="status"
          inputId="status"
          :options="statusOptions"
          :disabled="!statusOptions.length"
          :loading="!statusOptions.length"
          class="w-full"
          filter
          filterPlaceholder="Search by status"
        />
        <label for="status">Status</label>
      </FloatLabel>

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="voice_id"
          inputId="voice_id"
          :options="props.voiceOptions"
          :disabled="!props.voiceOptions?.length"
          :loading="!props.voiceOptions?.length"
          class="w-full"
          optionLabel="display_name"
          optionValue="uuid"
          filter
          filterPlaceholder="Search by voice"
          @change="onChangeVoiceVal($event)"
        />
        <label for="voice_id">Voice</label>
      </FloatLabel>

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="avatar_id"
          inputId="avatar_id"
          :options="props.avatarOptions"
          :disabled="!props.avatarOptions?.length"
          :loading="!props.avatarOptions?.length"
          class="w-full"
          optionLabel="display_name"
          optionValue="uuid"
          filter
          filterPlaceholder="Search by avatar"
        />
        <label for="avatar_id">Avatar</label>
      </FloatLabel>

      <PresentationConfiguration
        v-model="presentation"
        :theme-options="props.presentationThemeOptions"
        :image-asset-options="props.presentationImageOptions"
      />

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="llm_model"
          inputId="llm_model"
          :options="props.llmModelOptions"
          :disabled="!props.llmModelOptions?.length"
          :loading="!props.llmModelOptions?.length"
          class="w-full"
          optionLabel="display_name"
          optionValue="uuid"
          filter
          filterPlaceholder="Search by LLM model"
        />
        <label for="llm_model">LLM Model</label>
      </FloatLabel>

      <FloatLabel class="w-full md:w-14rem mb-3">
        <PDropdown
          v-model="stt_model"
          inputId="stt_model"
          :options="props.sttModelOptions"
          :disabled="!props.sttModelOptions?.length"
          :loading="!props.sttModelOptions?.length"
          class="w-full"
          optionLabel="display_name"
          optionValue="uuid"
          filter
          filterPlaceholder="Search by STT model"
        />
        <label for="stt_model">STT Model</label>
      </FloatLabel>

      <!-- <div class="relative mb-3">
        <span v-if="data_collection.length" class="multiselect-title-span"
          >Data Collection</span
        >
        <VMultiselect
          :class="{ 'multiselect-options': data_collection.length }"
          v-model="data_collection"
          :options="props.dataCollectionsGridOptions"
          :disabled="!props.dataCollectionsGridOptions.length"
          :multiple="true"
          :loading="!props.dataCollectionsGridOptions.length"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          placeholder="Data Collection"
          trackBy="id"
          label="name"
        />
      </div> -->

      <FloatLabel class="w-full md:w-80 mb-3" variant="in">
        <PMultiSelect
          v-model="selectedDataCollections"
          :options="props.dataCollectionsGridOptions"
          :selectAll="isSelectAllDataCollections"
          :disabled="!props.dataCollectionsGridOptions.length"
          optionLabel="name"
          optionValue="id"
          @selectall-change="onSelectAllDataCollectionsChange($event)"
          @change="onChangeDataCollectionsVal($event)"
          :virtualScrollerOptions="{ itemSize: 30 }"
          :loading="!props.dataCollectionsGridOptions.length"
          filter
          class="custom-multiselect"
          panelClass="custom-multiselect-panel"
          display="chip"
          id="dataCollectionsOptions"
          :maxSelectedLabels="2"
          filterPlaceholder="Search by data source name"
        >
          <template #value="slotProps">
            <div
              v-if="slotProps.value && slotProps.value.length"
              class="flex items-center"
            >
              <div v-if="slotProps.value.length > 2">
                {{ slotProps.value.length }} items selected
              </div>
              <div v-else class="flex">
                <div
                  v-for="id in slotProps.value"
                  :key="id"
                  class="p-multiselect-token"
                >
                  <span class="p-multiselect-token-label">{{
                    getDataCollectionName(id)
                  }}</span>
                  <span
                    class="p-multiselect-token-icon"
                    @click.stop="removeDataCollection(id)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="p-icon"
                    >
                      <path
                        d="M10.5 3.5L3.5 10.5"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M3.5 3.5L10.5 10.5"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <span v-else>
              {{ slotProps.placeholder }}
            </span>
          </template>
        </PMultiSelect>
        <label for="dataCollectionsOptions">Data Sources</label>
      </FloatLabel>

      <FloatLabel
        v-if="canManageTeamProfile"
        class="w-full mb-3"
      >
        <PTextarea
          v-model.trim="prompt"
          id="promptMessage"
          class="d-textarea min-h-228 w-full"
          autoResize
        />
        <label for="promptMessage">Prompt</label>
      </FloatLabel>
    </template>

    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            :disabled="
              !name ||
              !agent_type ||
              !welcome_message ||
              hasPresentationErrors ||
              (!prompt && canManageTeamProfile) ||
              !status ||
              isSubmitted
            "
            @click="onSubmit"
            type="button"
            class="btn btn-primary add-button mr-30"
          >
            Edit
            <span
              v-if="isSubmitted"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            @click="resetStoreData"
            type="button"
            class="btn btn-secondary cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>
